import { CheckoutSection } from 'server/types/pmts-overrides';
import { derivePriceBadgeText, WizardState } from './campaign-wizard';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  IcoChevronDown,
  IcoChevronUp,
  IcoTrash,
  IcoPlus,
  IcoSparkles,
  IcoEye,
} from '@components/icons';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { SectionEditor, SECTION_TYPE_META } from './section-editor';
import { rpx } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import { CampaignPageShell } from '@course/components/campaign/campaign-page-shell';
import { SectionRenderer } from '@course/components/campaign/section-renderer';

type Props = {
  state: WizardState;
  updateState: (partial: Partial<WizardState>) => void;
};

const SECTION_TYPE_LABELS: Record<string, string> = {
  hero: 'Hero',
  eyebrow: 'Eyebrow Text',
  content: 'Content',
  callout: 'Callout',
  schedule: 'Schedule Cards',
  valueStack: 'Benefits / Value Stack',
  featureGrid: 'Feature Grid',
  video: 'Video',
  trust: 'Trust Badges',
  integratedCheckout: 'Checkout Form',
  checkoutForm: 'Checkout Form (Standalone)',
  testimonials: 'Testimonials',
  faq: 'FAQ',
  instructor: 'Instructor',
  urgency: 'Urgency Bar',
  footer: 'Footer',
  cta: 'Call to Action',
};

const ADD_SECTION_TYPES: CheckoutSection['type'][] = [
  'hero',
  'eyebrow',
  'content',
  'callout',
  'schedule',
  'valueStack',
  'faq',
  'integratedCheckout',
  'instructor',
  'testimonials',
  'video',
  'trust',
  'urgency',
  'footer',
];

function createDefaultSection(type: CheckoutSection['type']): CheckoutSection {
  switch (type) {
    case 'hero':
      return {
        type: 'hero',
        settings: { gradientFrom: '#0089bd', gradientTo: '#013ea6' },
        content: { headline: '' },
      };
    case 'eyebrow':
      return { type: 'eyebrow', content: { text: '' } };
    case 'content':
      return {
        type: 'content',
        settings: { maxWidth: 'wide' },
        content: { blocks: [{ type: 'paragraph', text: '' }] },
      };
    case 'callout':
      return { type: 'callout', content: { icon: 'book', heading: '' } };
    case 'schedule':
      return {
        type: 'schedule',
        content: {
          items: [{ label: 'Day 1', title: '', description: '' }],
        },
      };
    case 'valueStack':
      return {
        type: 'valueStack',
        content: { items: [{ bold: '', text: '' }] },
      };
    case 'faq':
      return {
        type: 'faq',
        content: { heading: 'Frequently Asked Questions', items: [] },
      };
    case 'integratedCheckout':
      return { type: 'integratedCheckout', content: {} };
    case 'instructor':
      return {
        type: 'instructor',
        content: { name: '', bio: '' },
      };
    case 'testimonials':
      return {
        type: 'testimonials',
        content: { items: [] },
      };
    case 'video':
      return { type: 'video', content: { url: '' } };
    case 'trust':
      return {
        type: 'trust',
        content: { badges: ['secure-checkout', 'money-back'] },
      };
    case 'urgency':
      return { type: 'urgency', content: { text: '' } };
    case 'footer':
      return { type: 'footer', content: {} };
    case 'checkoutForm':
      return { type: 'checkoutForm' };
    case 'featureGrid':
      return {
        type: 'featureGrid',
        content: { items: [{ title: '', description: '' }] },
      };
    case 'cta':
      return {
        type: 'cta',
        content: { acceptText: 'Yes, add this!', declineText: 'No thanks' },
      };
    default:
      return { type: 'content', settings: { maxWidth: 'wide' }, content: { blocks: [] } };
  }
}

function AIGeneratePanel({ state, updateState }: Props) {
  const [text, setText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const selectedPrice = state.prices.find((p) => p.id === state.selectedPriceId);

  async function handleGenerate() {
    if (!text.trim() || !state.courseId) {
      return;
    }
    setGenerating(true);
    try {
      const result = await rpx.aiCheckoutBuilder.generateCheckoutPage({
        courseId: state.courseId,
        marketingText: text.trim(),
        priceInfo: {
          name: selectedPrice?.name || 'Price',
          amount: selectedPrice?.priceInCents || 0,
          paymentType: selectedPrice?.paymentType || 'onetime',
        },
      });
      if (result.config.sections.length > 0) {
        const sections = result.config.sections.map((s: any) => {
          if (s.type === 'checkoutForm') {
            return { type: 'integratedCheckout' as const, content: {} };
          }
          return s;
        });
        updateState({ sections });
        setCollapsed(true);
      }
    } catch (err) {
      showError(err);
    } finally {
      setGenerating(false);
    }
  }

  if (collapsed) {
    return (
      <div class="mb-6 flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
        <span class="text-green-700 font-medium">
          Sections generated from your content. Edit below to customize.
        </span>
        <button
          type="button"
          class="text-green-600 underline text-sm"
          onClick={() => setCollapsed(false)}
        >
          Generate again
        </button>
      </div>
    );
  }

  return (
    <div class="mb-6 bg-indigo-50/50 rounded-xl p-5">
      <div class="flex items-center gap-2 mb-1">
        <IcoSparkles class="size-4 text-indigo-400" />
        <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">AI Generate</h3>
      </div>
      <p class="text-sm text-gray-500 mb-3">
        Paste your marketing copy, Google Doc content, or event description below. AI will create
        the page sections automatically — you can then customize each one.
      </p>
      <textarea
        class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-32 mb-3"
        placeholder="Paste your marketing copy, event description, or page content here..."
        value={text}
        onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
      />
      <div class="flex items-center justify-between">
        <span class="text-xs text-gray-400">
          {text.length > 0 ? `${text.length.toLocaleString()} characters` : ''}
        </span>
        <BtnPrimary
          onClick={handleGenerate}
          isLoading={generating}
          disabled={!text.trim() || !state.courseId}
        >
          {generating ? 'Generating...' : 'Generate Sections'}
        </BtnPrimary>
      </div>
    </div>
  );
}

function PreviewModal({
  sections,
  brand,
  onClose,
}: {
  sections: CheckoutSection[];
  brand?: string;
  onClose(): void;
}) {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div style="position: fixed; inset: 0; z-index: 9999; background: #fff; overflow-y: auto">
      <div style="position: sticky; top: 0; z-index: 10; background: rgba(0,0,0,0.8); display: flex; align-items: center; justify-content: space-between; padding: 8px 16px">
        <span style="color: #fff; font-size: 13px; font-weight: 500">
          Preview — this is how the page will look to visitors
        </span>
        <button
          type="button"
          onClick={onClose}
          style="background: #fff; color: #111; border: none; border-radius: 6px; padding: 6px 16px; font-size: 13px; font-weight: 600; cursor: pointer"
        >
          Close Preview
        </button>
      </div>
      <CampaignPageShell brand={brand}>
        <SectionRenderer sections={sections} brand={brand} />
      </CampaignPageShell>
    </div>
  );
}

export function CheckoutStep({ state, updateState }: Props) {
  const [expandedIndex, setExpandedIndex] = useState<number | undefined>(undefined);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const addMenuRef = useRef<HTMLDivElement>(null);
  const selectedPrice = state.prices.find((p) => p.id === state.selectedPriceId);
  const priceBadgePlaceholder = selectedPrice ? derivePriceBadgeText(selectedPrice) : undefined;

  useEffect(() => {
    if (!showAddMenu) {
      return;
    }
    function handleClickOutside(e: MouseEvent) {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target as Node)) {
        setShowAddMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddMenu]);

  function updateSection(index: number, updated: CheckoutSection) {
    const newSections = [...state.sections];
    newSections[index] = updated;
    updateState({ sections: newSections });
  }

  function removeSection(index: number) {
    const newSections = state.sections.filter((_, i) => i !== index);
    updateState({ sections: newSections });
    setExpandedIndex(undefined);
  }

  function moveSection(index: number, direction: -1 | 1) {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= state.sections.length) {
      return;
    }
    const newSections = [...state.sections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    updateState({ sections: newSections });
    setExpandedIndex(newIndex);
  }

  function addSection(type: CheckoutSection['type']) {
    const section = createDefaultSection(type);
    const newSections = [...state.sections, section];
    updateState({ sections: newSections });
    setExpandedIndex(newSections.length - 1);
    setShowAddMenu(false);
  }

  return (
    <div>
      <div class="flex items-start justify-between mb-5">
        <div>
          <h2 class="text-lg font-semibold text-gray-900 mb-1">Checkout Page</h2>
          <p class="text-sm text-gray-500">
            Build your checkout page. Use AI to generate sections, or add them manually.
          </p>
        </div>
        <BtnSecondary onClick={() => setShowPreview(true)} class="gap-2 flex-shrink-0">
          <IcoEye class="size-4" />
          Preview
        </BtnSecondary>
      </div>

      <AIGeneratePanel state={state} updateState={updateState} />

      <div class="flex flex-col gap-2">
        {state.sections.map((section, i) => {
          const isExpanded = expandedIndex === i;
          const label = SECTION_TYPE_LABELS[section.type] || section.type;
          const meta = SECTION_TYPE_META[section.type];
          const Icon = meta?.icon;

          return (
            <div key={i} class="border border-gray-200 rounded-lg overflow-hidden">
              <div
                class="flex items-center gap-3 px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => setExpandedIndex(isExpanded ? undefined : i)}
              >
                <span class="text-xs font-semibold text-gray-400 w-6 text-center">{i + 1}</span>
                {Icon && <Icon class="size-4 text-gray-400 flex-shrink-0" />}
                <span class="flex-1 text-sm font-medium text-gray-900">{label}</span>
                <div class="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      moveSection(i, -1);
                    }}
                    disabled={i === 0}
                    class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    title="Move up"
                  >
                    <IcoChevronUp class="size-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      moveSection(i, 1);
                    }}
                    disabled={i === state.sections.length - 1}
                    class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    title="Move down"
                  >
                    <IcoChevronDown class="size-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSection(i);
                    }}
                    class="p-1 text-gray-400 hover:text-red-500"
                    title="Remove section"
                  >
                    <IcoTrash class="size-4" />
                  </button>
                </div>
              </div>
              {isExpanded && (
                <div class="p-4 border-t border-gray-200">
                  <SectionEditor
                    section={section}
                    onChange={(updated) => updateSection(i, updated)}
                    priceBadgePlaceholder={priceBadgePlaceholder}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div class="mt-4 flex items-center gap-3">
        <div class="relative" ref={addMenuRef}>
          <BtnSecondary onClick={() => setShowAddMenu(!showAddMenu)}>
            <IcoPlus class="size-4" />
            Add Section
          </BtnSecondary>
          {showAddMenu && (
            <div class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 w-80 max-h-80 overflow-y-auto">
              {ADD_SECTION_TYPES.map((type) => {
                const meta = SECTION_TYPE_META[type];
                const TypeIcon = meta?.icon;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => addSection(type)}
                    class="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-gray-700 flex items-center gap-3"
                  >
                    {TypeIcon && <TypeIcon class="size-4 text-gray-400 flex-shrink-0" />}
                    <div>
                      <span class="text-sm font-medium block">
                        {SECTION_TYPE_LABELS[type] || type}
                      </span>
                      {meta?.desc && <span class="text-xs text-gray-400">{meta.desc}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <BtnSecondary onClick={() => setShowPreview(true)}>Preview Page</BtnSecondary>
      </div>

      {showPreview && (
        <PreviewModal
          sections={state.sections}
          brand={state.brand}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}
